import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCoarsePointer } from "../../../hooks/useCoarsePointer";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../../base/hover-card/hover-card";
import { Skeleton } from "../../base/skeleton/skeleton";
import { ChatRetryRow } from "./chat-part-shell";
import { isCardEntityType, useEntityCard } from "./use-entity-card";
/**
 * The record behind an `entity://` mention, on hover (plan 020 §S3).
 *
 * The chip's label is the model's write-time snapshot; this is the record as it
 * is NOW — a rename shows through, and a soft delete arrives as the product's
 * own grayscale "(deleted)" treatment, which `AssigneeCard` applies itself. The
 * chip never changes state for either: it is what the assistant said.
 *
 * A desktop enhancement, deliberately. Keyboard, touch and screen-reader users
 * reach the same record by following the chip, which is why nothing here is the
 * only path to anything — including the retry below.
 */ /**
 * What the popover holds, in whichever of the four states the record's lookup
 * list is in.
 *
 * Rendered inside `HoverCardContent`, so it mounts on the first open and not
 * before — which is what makes the four org-wide lookup queries an effect of
 * asking to SEE a record rather than of a transcript merely containing a
 * mention. Once opened, `EntityCardSubscription` keeps them subscribed.
 */ /**
 * Keeps the row a row at popover width. The shared Item wraps by default and
 * carries page-level padding, so a long team name or email pushed the
 * department badge under the icon and broke the email mid-word. Nowrap holds
 * the badge in the row, and the tighter padding hands the text column the
 * width the Item's default `p-4 gap-4` was spending. The title needs BOTH
 * `block` and `w-auto`: as a flex container its text is an anonymous flex
 * item whose `min-width: auto` never shrinks below the longest word, and as a
 * `w-fit` block it sizes to that word — either way a long German compound
 * painted straight under the badge while the title BOX measured correctly.
 * As a full-width block it wraps at word boundaries, with `break-words` as
 * the last resort for a word wider than the column. The one-line clamp trades
 * the tail of a long email for a card that keeps its shape — the full record
 * stays one click away on the chip itself.
 */ const COMPACT_CARD_CLASS = "flex-nowrap gap-3 p-2 [&_[data-slot=item-content]]:min-w-0 [&_[data-slot=item-title]]:block [&_[data-slot=item-title]]:w-auto [&_[data-slot=item-title]]:break-words [&_[data-slot=item-description]]:line-clamp-1";
function EntityMentionCardContent({ data, entityType, entityId }) {
    const { t } = useTranslation();
    const state = useEntityCard(data, entityType, entityId, COMPACT_CARD_CLASS);
    switch(state.status){
        case "pending":
            return /*#__PURE__*/ _jsxs("div", {
                role: "status",
                "aria-label": t("chat.entityCard.loading"),
                className: "flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-8 w-8 shrink-0 rounded-full"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex min-w-0 flex-1 flex-col gap-1.5",
                        children: [
                            /*#__PURE__*/ _jsx(Skeleton, {
                                className: "h-3.5 w-28"
                            }),
                            /*#__PURE__*/ _jsx(Skeleton, {
                                className: "h-3 w-20"
                            })
                        ]
                    })
                ]
            });
        case "unavailable":
            // No retry: nothing would change. The list was fetched, and fetched WITH
            // tombstones, so an absent id is a record that is really gone.
            return /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: t("chat.entityCard.unavailable")
            });
        case "failed":
            // The same row the block-level parts draw, without their dashed frame —
            // this one is already inside a popover. The retry is mouse-only by library
            // contract: `HoverCardContent` forces `tabindex="-1"` onto every tabbable
            // node it holds, and the trigger closes on blur. Worth having anyway —
            // `retry: false` on the query client means one failed request settles an
            // org-wide list for the session, and healing it here heals every mention
            // on the page.
            return /*#__PURE__*/ _jsx(ChatRetryRow, {
                message: t("chat.entityCard.loadFailed"),
                onRetry: state.retry
            });
        case "ready":
            return state.card;
        default:
            {
                // `ReactNode` includes `undefined`, so a fifth state with no arm here
                // would draw an empty popover rather than fail.
                const unhandled = state;
                throw new Error(`unhandled entity mention card state: ${JSON.stringify(unhandled)}`);
            }
    }
}
/**
 * Holds the lookup subscriptions open after the card has closed again, and
 * draws nothing.
 *
 * `HoverCardContent` unmounts on close, and in the Omniscient app it holds the
 * only observer these four queries have — the route loader prefetches without
 * one. React Query cancels an in-flight fetch when its LAST observer leaves
 * (`retryer.cancel({ revert: true })`, reachable because every lookup `queryFn`
 * consumes its abort signal), so a hover shorter than the round trip would
 * abort the very fetch it started and the next hover would begin again from
 * nothing. One subscriber per chip that has opened once keeps the count above
 * zero for as long as the transcript is on screen; React Query dedupes them all
 * onto the same four queries.
 */ function EntityCardSubscription({ data, entityType, entityId }) {
    useEntityCard(data, entityType, entityId);
    return null;
}
/**
 * The trigger, for a type that has a card and a pointer that can hover.
 *
 * `children` is the link ELEMENT, not a component that renders one:
 * `HoverCardTrigger asChild` merges its ref and its pointer/focus handlers onto
 * whatever it is given, and a component boundary in between would swallow both.
 *
 * `openDelay` is left at the Radix default — a chip sits mid-sentence, and a
 * card that opens on the way past would fight reading.
 */ function CardEntityMention({ data, entityType, entityId, children }) {
    const coarsePointer = useCoarsePointer();
    const [everOpened, setEverOpened] = useState(false);
    // Nothing to mount where the card could never open: Radix ignores a touch
    // pointer on the trigger, so a trigger, a Popper root and their portal
    // machinery per chip would be dead weight. See `useCoarsePointer` for why
    // this is best-effort and why a tap works either way.
    if (coarsePointer) return children;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(HoverCard, {
                onOpenChange: (open)=>{
                    if (open) setEverOpened(true);
                },
                children: [
                    /*#__PURE__*/ _jsx(HoverCardTrigger, {
                        asChild: true,
                        children: children
                    }),
                    /*#__PURE__*/ _jsx(HoverCardContent, {
                        className: "w-80 p-2",
                        children: /*#__PURE__*/ _jsx(EntityMentionCardContent, {
                            data: data,
                            entityType: entityType,
                            entityId: entityId
                        })
                    })
                ]
            }),
            everOpened && /*#__PURE__*/ _jsx(EntityCardSubscription, {
                data: data,
                entityType: entityType,
                entityId: entityId
            })
        ]
    });
}
/**
 * Wraps a mention chip's link in its hover card, where there is one to show.
 *
 * The type gate is decided BEFORE any hook runs, in this component, and the
 * pointer query lives one component deeper: a `process`, `department` or
 * `artifact` chip has no card at any pointer, so it costs no `change` listener
 * on the shared `MediaQueryList` and no state of its own. Two components rather
 * than one early return, because hook order may not depend on a prop.
 */ export function EntityMentionHoverCard({ data, entityType, entityId, children }) {
    if (!isCardEntityType(entityType)) return children;
    return /*#__PURE__*/ _jsx(CardEntityMention, {
        data: data,
        entityType: entityType,
        entityId: entityId,
        children: children
    });
}

//# sourceMappingURL=entity-mention-hover-card.js.map